import { Component, OnInit, Optional, Inject } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-form-enrollment-policies',
  templateUrl: './form-enrollment-policies.component.html',
  styleUrls: ['./form-enrollment-policies.component.css']
})
export class FormEnrollmentPoliciesComponent implements OnInit {
  enrollmentPoliciesForm: FormGroup;
  /** Starts true so the "Allow Negative Enrollments" checkbox does not paint
   *  unchecked and then flip itself on once the config lands. */
  showProgressSpinner = true;
  /** Both initial loads must finish before the form is shown. */
  private pendingLoads = 2;
  successMessage: string;
  errorMessage: string;

  enrollmentAgreements: any[] = [];

  constructor(
    private fb: FormBuilder,
    private drupalRESTService: DrupalRESTService,
    @Optional() public dialogRef: MatDialogRef<FormEnrollmentPoliciesComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any
  ) {}

  ngOnInit(): void {
    this.enrollmentPoliciesForm = this.fb.group({
      allowNegativeEnrollments: [false],
      enrollmentAgreement: ['']
    });

    this.loadStudioConfig();
    this.loadEnrollmentAgreements();
  }

  /** Clears the spinner once every initial request has settled. */
  private settleLoad(): void {
    this.pendingLoads = Math.max(0, this.pendingLoads - 1);
    if (this.pendingLoads === 0) {
      this.showProgressSpinner = false;
    }
  }

  loadStudioConfig(): void {
    this.drupalRESTService
      .httpGET('/api_rest/v1/loadStudioConfig?config_field_name=field_studio_information_ref')
      .subscribe(
        (response) => {
          this.enrollmentPoliciesForm.patchValue({
            allowNegativeEnrollments:
              response?.['field_allow_negative_enrollments']?.[0]?.value === '1' ||
              response?.['field_allow_negative_enrollments']?.[0]?.value === true
          });
          this.settleLoad();
        },
        (error) => {
          LoggerService.error('Error loading studio config:', error);
          this.errorMessage = 'Error loading enrollment policies.';
          this.settleLoad();
        }
      );
  }

  loadEnrollmentAgreements(): void {
    this.drupalRESTService
      .httpGET('/api_rest/v1/loadEnrollmentAgreements')
      .subscribe(
        (response: any) => {
          this.enrollmentAgreements = Array.isArray(response) ? response : [];

          // Pre-select the current default agreement
          const defaultAgreement = this.enrollmentAgreements.find(
            a => a.field_is_default?.[0]?.value === '1'
          );
          if (defaultAgreement) {
            this.enrollmentPoliciesForm.patchValue({
              enrollmentAgreement: defaultAgreement.id
            });
          }
          this.settleLoad();
        },
        (error) => {
          LoggerService.error('Error loading enrollment agreements:', error);
          this.settleLoad();
        }
      );
  }

  save(): void {
    const formValues = this.enrollmentPoliciesForm.value;

    this.showProgressSpinner = true;
    this.successMessage = '';
    this.errorMessage = '';

    // Save studio config (allow negative enrollments)
    const configPost = {
      config_field_name: 'field_studio_information_ref',
      config_data: {
        field_allow_negative_enrollments: [{ value: formValues.allowNegativeEnrollments ? '1' : '0' }]
      }
    };

    this.drupalRESTService
      .httpPOST('/api_rest/v1/saveStudioConfig', configPost)
      .subscribe(
        () => {
          // If an agreement was selected, set it as the default
          if (formValues.enrollmentAgreement) {
            this.drupalRESTService
              .httpPOST('/api_rest/v1/setDefaultEnrollmentAgreement', {
                agreement_id: formValues.enrollmentAgreement
              })
              .subscribe(
                () => {
                  this.showProgressSpinner = false;
                  if (this.dialogRef) {
                    this.dialogRef.close(true);
                  }
                },
                (error) => {
                  LoggerService.error('Error setting default agreement:', error);
                  this.errorMessage = 'Error saving agreement selection.';
                  this.showProgressSpinner = false;
                }
              );
          } else {
            this.showProgressSpinner = false;
            if (this.dialogRef) {
              this.dialogRef.close(true);
            }
          }
        },
        (error) => {
          LoggerService.error('Error saving enrollment policies:', error);
          this.errorMessage = 'Error saving enrollment policies.';
          this.showProgressSpinner = false;
        }
      );
  }
}
